import * as React from 'react';
import { createPortal } from 'react-dom';
import { ArrayControlProps, Actions } from '@jsonforms/core';
import { useJsonForms } from '@jsonforms/react';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import { DndContext } from '@dnd-kit/core';
import { Box } from '@leagueplatform/genesis-core';
import type { GDSStyleObject } from '@leagueplatform/genesis-core';
import type {
  EntityFormArrayControlProps,
  SortableItemRenderProps,
} from '../../types/controls';
import { useEntityFormArrayControl } from '../../hooks/use-entity-form-array-control/use-entity-form-array-control';
import { useSortableArray } from '../../hooks/use-sortable-array/use-sortable-array';
import type { UseSortableArrayReturn } from '../../hooks/use-sortable-array/use-sortable-array';
import { Hidden } from '../hidden/hidden.component';

/*
 * EntityFormArrayControl encapsulates taking in the JsonForms props, calling useEntityFormArrayControl to get the correct annotation/props, and passing those to the arrayControl. The renderControl prop lets you completely take over how the control is rendered.
 * It automatically provides drag-and-drop functionality through helper props.
 */

/**
 * Helper function to copy computed styles from one element to another as inline styles.
 * Used for cloning drag overlay elements with their visual appearance intact.
 */
const copyComputedStylesToInline = (
  sourceElement: Element,
  targetElement: HTMLElement,
) => {
  if (typeof window === 'undefined') return;
  const computedStyle = window.getComputedStyle(sourceElement);

  // Copy all computed styles to inline styles
  Array.from(computedStyle).forEach((key) => {
    targetElement.style.setProperty(
      key,
      computedStyle.getPropertyValue(key),
      computedStyle.getPropertyPriority(key),
    );
  });
};

export interface EntityFormArrayControlOwnProps extends ArrayControlProps {
  renderControl: (props: EntityFormArrayControlProps) => JSX.Element;
}

type SortableItemDependencies = {
  useSortableItem: UseSortableArrayReturn['useSortableItem'];
  itemNodesRef: React.MutableRefObject<Map<string, HTMLElement>>;
  activeDragId: string | null;
};

const SortableItemDependenciesContext =
  React.createContext<SortableItemDependencies | null>(null);

const SortableItem: React.FC<{
  id: string;
  children: (
    dragHandleProps: SortableItemRenderProps['dragHandleProps'],
  ) => React.ReactNode;
  disabled?: boolean;
  containerCss?: GDSStyleObject;
}> = ({ id, children, disabled = false, containerCss }) => {
  const context = React.useContext(SortableItemDependenciesContext);

  if (!context) {
    throw new Error(
      'SortableItem must be used within a SortableItemDependenciesContext.Provider',
    );
  }

  const { useSortableItem, itemNodesRef, activeDragId } = context;

  const { containerProps, isDragging, isSettling, dragHandleProps } =
    useSortableItem({ id, disabled });

  const { ref: itemRef, style } = containerProps;

  const combinedRef = React.useCallback(
    (node: HTMLElement | null) => {
      itemRef(node);
      if (node) {
        itemNodesRef.current.set(id, node);
      } else {
        itemNodesRef.current.delete(id);
      }
    },
    [id, itemNodesRef, itemRef],
  );

  const isBeingDragged = isDragging && id === activeDragId;

  return (
    <Box
      role="presentation"
      ref={combinedRef}
      style={style}
      css={{
        ...containerCss,
        position: 'relative',
        cursor: isDragging ? 'grabbing' : 'inherit',
        visibility: isBeingDragged ? 'hidden' : 'visible',
      }}
    >
      {children(dragHandleProps)}
      {isSettling && (
        <Box
          css={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'white',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        />
      )}
    </Box>
  );
};
export const EntityFormArrayControl: React.FC<
  EntityFormArrayControlOwnProps
> = ({ renderControl, ...props }) => {
  const ctx = useJsonForms();
  const { dispatch } = ctx;
  const entityFormArrayControlProps = useEntityFormArrayControl(props);
  const { hidden, data, path, id, itemLabel, enabled } =
    entityFormArrayControlProps;

  const [relationshipItemCountOverride, setRelationshipItemCountOverride] =
    React.useState<number | undefined>(undefined);

  // Generate sortable IDs for all items - only regenerate if array length or id changes
  const dataLength = React.useMemo(() => {
    if (typeof relationshipItemCountOverride === 'number') {
      return relationshipItemCountOverride;
    }
    return Array.isArray(data) ? data.length : 0;
  }, [relationshipItemCountOverride, data]);
  const isControlEnabled = enabled !== false;
  const isDragEnabled = isControlEnabled && dataLength > 1;

  const sortableIds = React.useMemo(
    () => Array.from({ length: dataLength }, (_, index) => `${id}-${index}`),
    [dataLength, id],
  );

  // Ref to store the after-reorder callback from array controls
  const afterReorderCallbackRef = React.useRef<
    ((fromIndex: number, toIndex: number) => void) | null
  >(null);

  // Refs to store latest values for handleReorder
  const dataRef = React.useRef(data);
  const dispatchRef = React.useRef(dispatch);
  const pathRef = React.useRef(path);

  // Update refs when values change
  React.useEffect(() => {
    dataRef.current = data;
    dispatchRef.current = dispatch;
    pathRef.current = path;
  });

  // Function for array controls to register their after-reorder callback
  const registerAfterReorderCallback = React.useCallback(
    (callback: ((fromIndex: number, toIndex: number) => void) | null) => {
      afterReorderCallbackRef.current = callback;
    },
    [],
  );

  // The reorder handler that will be called by drag-and-drop - stable reference
  const handleReorder = React.useCallback(
    (fromIndex: number, toIndex: number) => {
      // Perform the base reorder when JsonForms data is available
      if (Array.isArray(dataRef.current)) {
        const newArray = arrayMove(dataRef.current, fromIndex, toIndex);
        dispatchRef.current?.(Actions.update(pathRef.current, () => newArray));
      }

      // Invoke any registered callback so specialized array controls can stay in sync
      if (afterReorderCallbackRef.current) {
        afterReorderCallbackRef.current(fromIndex, toIndex);
      }
    },
    [], // No dependencies - completely stable
  );

  // Set up drag-and-drop functionality
  const {
    dndContextProps,
    sortableContextProps,
    containerWrapperProps,
    useSortableItem,
    FocusContext,
    focusContextValue,
    SettlingContext,
    settlingContextValue,
    activeDragId,
    DragOverlay,
  } = useSortableArray({
    items: sortableIds,
    onReorder: handleReorder,
    disabled: !isDragEnabled,
    itemLabel: itemLabel || 'Item',
  });

  // Store refs to cloned DOM nodes for DragOverlay
  const itemNodesRef = React.useRef<Map<string, HTMLElement>>(new Map());

  // Helper function to create sortable IDs
  const createSortableId = React.useCallback(
    (index: number) => `${id}-${index}`,
    [id],
  );

  const sortableItemContextValue = React.useMemo(
    () => ({ useSortableItem, itemNodesRef, activeDragId }),
    [useSortableItem, activeDragId],
  );

  // Wrapper component for sortable container
  const SortableWrapper = React.useCallback(
    ({ children }: { children: React.ReactNode }) => (
      <div {...containerWrapperProps}>
        {isDragEnabled ? (
          <SortableContext {...sortableContextProps}>
            {children}
          </SortableContext>
        ) : (
          children
        )}
      </div>
    ),
    [containerWrapperProps, sortableContextProps, isDragEnabled],
  );

  // Augment props with drag-and-drop helpers
  const propsWithDragAndDrop: EntityFormArrayControlProps = React.useMemo(
    () => ({
      ...entityFormArrayControlProps,
      enabled: isControlEnabled,
      createSortableId,
      SortableItem,
      SortableWrapper,
      registerAfterReorderCallback,
      activeDragId,
      relationshipItemCount: relationshipItemCountOverride,
      setRelationshipItemCount: setRelationshipItemCountOverride,
    }),
    [
      entityFormArrayControlProps,
      isControlEnabled,
      createSortableId,
      SortableWrapper,
      registerAfterReorderCallback,
      activeDragId,
      relationshipItemCountOverride,
    ],
  );

  // Create overlay content by cloning the DOM node with computed styles
  const overlayContent = React.useMemo(() => {
    if (!isDragEnabled || !activeDragId) return null;

    const node = itemNodesRef.current.get(activeDragId);
    if (!node) return null;

    // Clone the node deeply
    const clonedNode = node.cloneNode(true) as HTMLElement;

    // Copy computed styles from original to clone
    copyComputedStylesToInline(node, clonedNode);

    // Copy computed styles for all children
    const sourceChildren = node.querySelectorAll('*');
    const clonedChildren = clonedNode.querySelectorAll('*');
    sourceChildren.forEach((sourceChild, index) => {
      if (clonedChildren[index]) {
        copyComputedStylesToInline(
          sourceChild,
          clonedChildren[index] as HTMLElement,
        );
      }
    });

    // Remove pointer events from the clone
    clonedNode.style.pointerEvents = 'none';
    clonedChildren.forEach((child) => {
      const element = child as HTMLElement;
      element.style.pointerEvents = 'none';
    });

    // Create a wrapper div and append the clone
    return (
      <Box
        css={{
          cursor: 'grabbing',
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)',
          opacity: 0.85,
        }}
        ref={(el) => {
          if (el && el.childNodes.length === 0) {
            el.appendChild(clonedNode);
          }
        }}
      />
    );
  }, [activeDragId, isDragEnabled]);

  if (hidden) {
    return <Hidden {...entityFormArrayControlProps} />;
  }

  // Wrap the rendered control with DndContext and Focus/Settling providers
  return (
    <FocusContext.Provider value={focusContextValue}>
      <SettlingContext.Provider value={settlingContextValue}>
        <DndContext {...dndContextProps}>
          <SortableItemDependenciesContext.Provider
            value={sortableItemContextValue}
          >
            {renderControl(propsWithDragAndDrop)}
          </SortableItemDependenciesContext.Provider>
          {isDragEnabled &&
            overlayContent &&
            (typeof document !== 'undefined' ? (
              createPortal(
                <DragOverlay dropAnimation={null} zIndex={999999}>
                  {overlayContent}
                </DragOverlay>,
                document.body,
              )
            ) : (
              <DragOverlay dropAnimation={null} zIndex={999999}>
                {overlayContent}
              </DragOverlay>
            ))}
        </DndContext>
      </SettlingContext.Provider>
    </FocusContext.Provider>
  );
};
